import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { buildAttachmentUrl } from "../../api/transactionApi";
import "./style.css";

const titleCase = (s) => {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.substring(1).toLowerCase() : w))
    .join(" ");
};

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const Row = ({ label, value }) => {
  if (!value && value !== 0) return null;
  const v = String(value).trim();
  if (!v) return null;
  return (
    <div className="txn-detail-row">
      <span className="txn-detail-label">{label}</span>
      <span className="txn-detail-value">{v}</span>
    </div>
  );
};

const TransactionHistoryDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const record = location?.state?.record;
  const [downloading, setDownloading] = useState(false);

  if (!record) {
    return (
      <section className="txn-history-page">
        <div className="container">
          <div className="txn-card">
            <div className="txn-error">
              <p>{t("transaction_history.no_transaction_selected")}</p>
              <button
                type="button"
                className="txn-btn-gold"
                style={{ maxWidth: 220, margin: "0 auto" }}
                onClick={() => navigate("/transaction-history")}
              >
                {t("transaction_history.back_to_list")}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const company = titleCase(record.insurance_company);
  const product = titleCase(record.product_name);
  const isTravel = record.product_id === 15;
  const attachmentUrl = buildAttachmentUrl(record.attachment);

  const onDownload = async () => {
    if (!attachmentUrl) return;
    setDownloading(true);
    try {
      const response = await fetch(attachmentUrl);
      if (!response.ok) throw new Error("Failed to fetch document");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const fileName = `${product}-${record.policy_no || record.quote_no || "policy"}.pdf`;
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // Fallback — open in new tab if direct download fails (e.g. CORS).
      window.open(attachmentUrl, "_blank", "noopener,noreferrer");
      toast.info(t("transaction_history.opening_in_new_tab"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="txn-history-page">
      <div className="container">
        <div className="txn-card txn-detail-card">
          <h2 className="txn-detail-title">
            {product} {t("transaction_history.insurance")}
          </h2>
          {company && <div className="txn-detail-company">{company}</div>}

          <div className="txn-section-bar">
            {t("transaction_history.policy_section")}
          </div>
          <Row label={t("transaction_history.policy_no")} value={record.policy_no} />
          <Row label={t("transaction_history.quote_no")} value={record.quote_no} />
          <Row
            label={t("transaction_history.business_status")}
            value={record.business_status}
          />
          <Row
            label={t("transaction_history.payment_status")}
            value={record.payment_status}
          />
          <Row
            label={t("transaction_history.premium")}
            value={
              record.premium_amount
                ? `${record.premium_amount} ${t("transaction_history.omr")}`
                : ""
            }
          />
          <Row
            label={t("transaction_history.policy_start")}
            value={formatDate(record.policy_start_date)}
          />
          <Row
            label={t("transaction_history.policy_expiry")}
            value={formatDate(record.policy_expiry_date)}
          />

          {isTravel && (record.travel_from || record.travel_to) && (
            <>
              <div className="txn-section-bar">
                {t("transaction_history.trip_section")}
              </div>
              <Row
                label={t("transaction_history.travel_from")}
                value={record.travel_from}
              />
              <Row
                label={t("transaction_history.travel_to")}
                value={record.travel_to}
              />
            </>
          )}

          {!isTravel && record.plate_no && (
            <>
              <div className="txn-section-bar">
                {t("transaction_history.vehicle_section")}
              </div>
              <Row
                label={t("transaction_history.plate_no")}
                value={[record.plate_character, record.plate_no]
                  .filter(Boolean)
                  .join(" ")}
              />
              <Row
                label={t("transaction_history.vehicle_make")}
                value={record.vehicle_make}
              />
              <Row
                label={t("transaction_history.vehicle_model")}
                value={record.vehicle_model}
              />
              <Row
                label={t("transaction_history.chassis_no")}
                value={record.chassis_no}
              />
              <Row
                label={t("transaction_history.engine_no")}
                value={record.engine_no}
              />
            </>
          )}

          <div className="txn-section-bar">
            {t("transaction_history.transaction_section")}
          </div>
          <Row
            label={t("transaction_history.transaction_date")}
            value={formatDate(record.txn_date)}
          />
          <Row
            label={t("transaction_history.transaction_ref")}
            value={record.txn_ref_id}
          />
          <Row
            label={t("transaction_history.mobile_no")}
            value={record.mobile_no}
          />

          <button
            type="button"
            className="txn-btn-gold"
            disabled={!attachmentUrl || downloading}
            onClick={onDownload}
          >
            {downloading
              ? t("transaction_history.opening")
              : attachmentUrl
                ? t("transaction_history.download_policy")
                : t("transaction_history.no_policy_document")}
          </button>

          <button
            type="button"
            className="txn-switch-btn"
            style={{ marginTop: 14, width: "100%" }}
            onClick={() => navigate(-1)}
          >
            ← {t("transaction_history.back")}
          </button>
        </div>
      </div>
    </section>
  );
};

export default TransactionHistoryDetail;
